import React, { useState } from 'react';

const EarningsSummary = ({ walletBalance }) => {
  const [activeTab, setActiveTab] = useState('today');

  const data = {
    today: {
      earnings: 3240,
      trips: 8,
      hours: 5.5,
      topEarning: 850,
    },
    week: {
      earnings: 18750,
      trips: 47,
      hours: 38,
      topEarning: 1200,
    },
  };

  const current = data?.[activeTab];

  const dailyBreakdown = [
    { day: 'Mon', amount: 2800, max: 4000 },
    { day: 'Tue', amount: 3500, max: 4000 },
    { day: 'Wed', amount: 1900, max: 4000 },
    { day: 'Thu', amount: 4000, max: 4000 },
    { day: 'Fri', amount: 3240, max: 4000, today: true },
    { day: 'Sat', amount: 0, max: 4000 },
    { day: 'Sun', amount: 0, max: 4000 },
  ];

  return (
    <div className="space-y-3">
      {/* Wallet balance card */}
      <div className="rounded-2xl p-4 relative overflow-hidden" style={{
        background: 'linear-gradient(135deg, #0a1628 0%, #1a365d 50%, #0a1628 100%)'
      }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-[#ffd700] blur-3xl" />
          <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full bg-blue-500 blur-3xl" />
        </div>
        <div className="relative">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="caption-text text-white/40 uppercase tracking-wider">Wallet Balance</p>
              <p className="text-[#ffd700] font-black text-2xl mt-0.5">KSh {walletBalance?.toLocaleString() || '12,450'}</p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-[#ffd700]/10 border border-[#ffd700]/20 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="#ffd700" className="w-5 h-5">
                <path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
              </svg>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 bg-green-500/20 rounded-full px-2.5 py-1 border border-green-500/20">
              <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
              <span className="text-green-400 text-xs font-semibold">M-Pesa Linked</span>
            </div>
            <button className="text-[#ffd700]/70 text-xs font-medium hover:text-[#ffd700] transition-colors">
              Withdraw →
            </button>
          </div>
        </div>
      </div>
      {/* Earnings tabs */}
      <div className="rounded-2xl bg-[#0f2040] border border-white/8 overflow-hidden">
        {/* Tab switcher */}
        <div className="flex border-b border-white/8">
          {['today', 'week']?.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 text-sm font-semibold capitalize transition-all ${
                activeTab === tab
                  ? 'text-[#ffd700] border-b-2 border-[#ffd700]'
                  : 'text-white/40 hover:text-white/60'
              }`}
            >
              {tab === 'today' ? "Today" : "This Week"}
            </button>
          ))}
        </div>

        <div className="p-4">
          {/* Main earnings */}
          <div className="flex items-end justify-between mb-4">
            <div>
              <p className="caption-text text-white/40 mb-1">Total Earned</p>
              <p className="heading-1 text-white">KSh {current?.earnings?.toLocaleString()}</p>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1 justify-end text-green-400 mb-1">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
                  <path d="M7 14l5-5 5 5z"/>
                </svg>
                <span className="caption-text font-semibold text-green-400">+12%</span>
              </div>
              <p className="caption-text text-white/30">vs yesterday</p>
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            {[
              { label: 'Trips', value: current?.trips, icon: '🚗' },
              { label: 'Hours', value: `${current?.hours}h`, icon: '⏱' },
              { label: 'Best Trip', value: `KSh ${current?.topEarning}`, icon: '⭐' },
            ]?.map(stat => (
              <div key={stat?.label} className="bg-white/5 rounded-xl p-2.5 text-center">
                <p className="text-base mb-0.5">{stat?.icon}</p>
                <p className="heading-3 text-white">{stat?.value}</p>
                <p className="caption-text text-white/30">{stat?.label}</p>
              </div>
            ))}
          </div>

          {/* Weekly bar chart */}
          {activeTab === 'week' && (
            <div>
              <p className="caption-text text-white/30 mb-2 uppercase tracking-wider">Daily Breakdown</p>
              <div className="flex items-end gap-1.5 h-16">
                {dailyBreakdown?.map(day => (
                  <div key={day?.day} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full rounded-t-md relative" style={{ height: '48px' }}>
                      <div
                        className={`absolute bottom-0 w-full rounded-t-md transition-all duration-500 ${
                          day?.today ? 'bg-[#ffd700]' : day?.amount > 0 ? 'bg-[#ffd700]/40' : 'bg-white/5'
                        }`}
                        style={{ height: `${(day?.amount / day?.max) * 100}%` }}
                      />
                    </div>
                    <span className={`text-[9px] font-medium ${day?.today ? 'text-[#ffd700]' : 'text-white/30'}`}>
                      {day?.day}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EarningsSummary;
